import { setCookie } from "cookies-next";

// external dependencies
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";

// types

import { COOKIE_CONFIG } from "@/config";
import { GLOBAL_ERROR_MESSAGE } from "@/constants/validation-message";
import { IApiError } from "@/interface/apiError.types";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import loginSchema from "@/schema/login";

// internal utilities and services
import { loginUser, resend2FA, verify2FA } from "@/services/auth.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/nextjs";
import { z } from "zod";

interface LoginApiResponse {
  message?: string;
  requiresTwoFactor?: boolean;
}

const useLogin = () => {
  const router = useRouter();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState<boolean>(false);
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(5).fill(""));
  const [lastLoginPayload, setLastLoginPayload] = useState<
    z.infer<typeof loginSchema> | null
  >(null);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const { mutate: login, isPending: loginLoading } = useMutation({
    mutationFn: loginUser,
    onSuccess: (response: LoginApiResponse) => {
      const isTwoFactorRequired = Boolean(response?.requiresTwoFactor);

      if (isTwoFactorRequired) {
        setRequiresTwoFactor(true);
        showToast(
          TOAST_TYPES.success,
          response?.message ?? "Verification code sent to your email."
        );
        return;
      }
     

      setCookie(COOKIE_CONFIG.loggedIn, "true");
      showToast(TOAST_TYPES.success, "Login successful");
      router.replace("/");
      router.refresh();
    },

    onError: (error: IApiError) => {
      Sentry.captureException("Login credentials are invalid", {
        extra: {
          error,
        },
      });

      const errors = error?.response?.data;
      showToast(TOAST_TYPES.error, errors?.message ?? GLOBAL_ERROR_MESSAGE);
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    setLastLoginPayload(values);
    login(values);
  };

  const { mutate: verifyOtp, isPending: verifyOtpLoading } = useMutation({
    mutationFn: verify2FA,
    onSuccess: (response: LoginApiResponse) => {
      setCookie(COOKIE_CONFIG.loggedIn, "true");
      showToast(TOAST_TYPES.success, response?.message ?? "Login successful");
      router.refresh();
    },
    onError: (error: IApiError) => {
      const errors = error?.response?.data;
      showToast(TOAST_TYPES.error, errors?.message ?? GLOBAL_ERROR_MESSAGE);
    },
  });

  const { mutate: resendOtpApi, isPending: resendOtpLoading } = useMutation({
    mutationFn: resend2FA,
    onSuccess: (response: LoginApiResponse) => {
      showToast(
        TOAST_TYPES.success,
        response?.message ?? "Verification code sent to your email."
      );
    },
    onError: (error: IApiError) => {
      const errors = error?.response?.data;
      showToast(TOAST_TYPES.error, errors?.message ?? GLOBAL_ERROR_MESSAGE);
    },
  });

  const handleOtpDigitChange = (index: number, value: string) => {
    const sanitizedValue = value.replace(/\D/g, "").slice(-1);
    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = sanitizedValue;
      return next;
    });
  };

  const resendOtp = () => {
    if (!lastLoginPayload) {
      showToast(
        TOAST_TYPES.error,
        "Session expired. Please enter your credentials again."
      );
      setRequiresTwoFactor(false);
      return;
    }

    resendOtpApi({ email: lastLoginPayload.username });
  };

  const onVerifyOtp = () => {
    if (!lastLoginPayload) {
      showToast(
        TOAST_TYPES.error,
        "Session expired. Please enter your credentials again."
      );
      setRequiresTwoFactor(false);
      return;
    }

    const otp = otpDigits.join("");
    if (otp.length !== 5) {
      showToast(TOAST_TYPES.error, "Please enter a valid 5-digit OTP.");
      return;
    }

    verifyOtp({
      email: lastLoginPayload.username,
      otp,
      remember: true,
    });
  };

  return {
    form,
    onSubmit,
    loginLoading,
    showPassword,
    togglePasswordVisibility,
    requiresTwoFactor,
    otpDigits,
    handleOtpDigitChange,
    resendOtp,
    onVerifyOtp,
    verifyOtpLoading,
    resendOtpLoading,
  };
};

export default useLogin;
